/**
 * ComplianceCoverageRadar
 *
 * Radar / spider chart showing compliance framework coverage across multiple
 * frameworks. Pure SVG rendering -- no external charting libraries required.
 *
 * Features:
 *   - Concentric reference rings at 25%, 50%, 75%, 100%
 *   - Filled polygon for current coverage values
 *   - Dashed polygon for target coverage values
 *   - Framework labels with monospace coverage percentages
 *   - Hover caption with detailed breakdown (controls satisfied / total)
 *   - Click on a framework label to invoke onFrameworkClick
 *
 * Uses Fluent UI v9 makeStyles / tokens for the card wrapper.
 */
import React, { useMemo, useState, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Caption1,
  Subtitle2,
} from '@fluentui/react-components';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface FrameworkData {
  name: string;
  coverage: number; // 0-1
  target?: number; // 0-1
  controlsTotal?: number;
  controlsSatisfied?: number;
}

export interface ComplianceCoverageRadarProps {
  frameworks?: FrameworkData[];
  size?: number; // SVG size, default 320
  onFrameworkClick?: (name: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Default mock data                                                  */
/* ------------------------------------------------------------------ */

const DEFAULT_FRAMEWORKS: FrameworkData[] = [
  { name: 'SOC2', coverage: 0.83, target: 0.95, controlsTotal: 120, controlsSatisfied: 100 },
  { name: 'ISO27001', coverage: 0.72, target: 0.90, controlsTotal: 93, controlsSatisfied: 67 },
  { name: 'NIST', coverage: 0.68, target: 0.85, controlsTotal: 110, controlsSatisfied: 75 },
  { name: 'CIS', coverage: 0.75, target: 0.90, controlsTotal: 80, controlsSatisfied: 60 },
];

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const RING_LEVELS = [0.25, 0.5, 0.75, 1.0];
const COVERAGE_FILL = 'rgba(0, 120, 212, 0.2)';
const COVERAGE_STROKE = '#0078D4';
const TARGET_STROKE = '#999999';
const RING_STROKE = '#E8E8E8';
const AXIS_STROKE = '#D0D0D0';
const LABEL_FONT_SIZE = 12;
const PERCENT_FONT_SIZE = 11;

/* ------------------------------------------------------------------ */
/*  Geometry helpers                                                    */
/* ------------------------------------------------------------------ */

/** Convert a framework index to an angle (radians), starting from top (12 o'clock). */
function indexToAngle(index: number, total: number): number {
  return (Math.PI * 2 * index) / total - Math.PI / 2;
}

/** Get the (x, y) position on the radar for a given axis index and radius fraction (0-1). */
function polarToCartesian(
  cx: number,
  cy: number,
  maxRadius: number,
  index: number,
  total: number,
  fraction: number,
): { x: number; y: number } {
  const angle = indexToAngle(index, total);
  const r = maxRadius * fraction;
  return {
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  };
}

/** Build an SVG polygon points string for a set of fraction values. */
function buildPolygonPoints(
  cx: number,
  cy: number,
  maxRadius: number,
  values: number[],
): string {
  return values
    .map((v, i) => {
      const { x, y } = polarToCartesian(cx, cy, maxRadius, i, values.length, v);
      return `${x},${y}`;
    })
    .join(' ');
}

/** Build an SVG polygon points string for a concentric ring at a given level. */
function buildRingPoints(
  cx: number,
  cy: number,
  maxRadius: number,
  level: number,
  sides: number,
): string {
  return Array.from({ length: sides }, (_, i) => {
    const { x, y } = polarToCartesian(cx, cy, maxRadius, i, sides, level);
    return `${x},${y}`;
  }).join(' ');
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  chartWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
    position: 'relative' as const,
  },

  tooltip: {
    position: 'absolute' as const,
    pointerEvents: 'none' as const,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    boxShadow: tokens.shadow8,
    zIndex: 10,
    whiteSpace: 'nowrap' as const,
    lineHeight: '1.5',
  },

  tooltipName: {
    fontWeight: 600 as const,
    color: tokens.colorNeutralForeground1,
    marginBottom: '2px',
    fontSize: tokens.fontSizeBase300,
  },

  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  tooltipValue: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
  },

  legendRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalXS,
  },

  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },

  legendSwatch: {
    width: '16px',
    height: '3px',
    borderRadius: '2px',
    flexShrink: 0,
  },
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ComplianceCoverageRadar: React.FC<ComplianceCoverageRadarProps> = ({
  frameworks,
  size = 320,
  onFrameworkClick,
}) => {
  const styles = useStyles();
  const data = useMemo(() => frameworks ?? DEFAULT_FRAMEWORKS, [frameworks]);
  const count = data.length;

  // Hover state
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Geometry
  const cx = size / 2;
  const cy = size / 2;
  const labelMargin = 50; // room for labels around the outer edge
  const maxRadius = size / 2 - labelMargin;

  // Coverage polygon
  const coveragePoints = useMemo(
    () => buildPolygonPoints(cx, cy, maxRadius, data.map((f) => f.coverage)),
    [cx, cy, maxRadius, data],
  );

  // Target polygon (only if at least one framework has a target)
  const hasTargets = data.some((f) => f.target !== undefined);
  const targetPoints = useMemo(
    () =>
      hasTargets
        ? buildPolygonPoints(
            cx,
            cy,
            maxRadius,
            data.map((f) => f.target ?? f.coverage),
          )
        : '',
    [cx, cy, maxRadius, data, hasTargets],
  );

  // Handlers
  const handleLabelClick = useCallback(
    (name: string) => {
      onFrameworkClick?.(name);
    },
    [onFrameworkClick],
  );

  const handleMouseEnter = useCallback(
    (index: number, event: React.MouseEvent) => {
      setHoveredIndex(index);
      const rect = (event.currentTarget as HTMLElement).closest('div')?.getBoundingClientRect();
      if (rect) {
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10,
        });
      }
    },
    [],
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (hoveredIndex === null) return;
      const rect = (event.currentTarget as HTMLElement).closest('div')?.getBoundingClientRect();
      if (rect) {
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10,
        });
      }
    },
    [hoveredIndex],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  // Tooltip data
  const hoveredFramework = hoveredIndex !== null ? data[hoveredIndex] : null;

  return (
    <div className={styles.root} role="region" aria-label="Compliance Coverage Radar">
      <div className={styles.header}>
        <Subtitle2>Framework Coverage</Subtitle2>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          {count} framework{count !== 1 ? 's' : ''} tracked
        </Caption1>
      </div>

      <Card>
        <div
          className={styles.chartWrapper}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={`Radar chart showing coverage for ${data.map((f) => f.name).join(', ')}`}
          >
            {/* Concentric reference rings */}
            {RING_LEVELS.map((level) => (
              <polygon
                key={`ring-${level}`}
                points={buildRingPoints(cx, cy, maxRadius, level, count)}
                fill="none"
                stroke={RING_STROKE}
                strokeWidth={level === 1.0 ? 1.5 : 0.8}
              />
            ))}

            {/* Ring level labels (right side) */}
            {RING_LEVELS.map((level) => {
              const { x, y } = polarToCartesian(cx, cy, maxRadius, 0, count, level);
              return (
                <text
                  key={`ring-label-${level}`}
                  x={x + 4}
                  y={y - 4}
                  fontSize={9}
                  fill="#AAAAAA"
                  fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace"
                >
                  {Math.round(level * 100)}%
                </text>
              );
            })}

            {/* Axis lines from center to each vertex */}
            {data.map((_, i) => {
              const outer = polarToCartesian(cx, cy, maxRadius, i, count, 1);
              return (
                <line
                  key={`axis-${i}`}
                  x1={cx}
                  y1={cy}
                  x2={outer.x}
                  y2={outer.y}
                  stroke={AXIS_STROKE}
                  strokeWidth={0.8}
                />
              );
            })}

            {/* Target polygon (dashed) */}
            {hasTargets && (
              <polygon
                points={targetPoints}
                fill="none"
                stroke={TARGET_STROKE}
                strokeWidth={1.5}
                strokeDasharray="6 4"
              />
            )}

            {/* Coverage polygon (filled) */}
            <polygon
              points={coveragePoints}
              fill={COVERAGE_FILL}
              stroke={COVERAGE_STROKE}
              strokeWidth={2}
              strokeLinejoin="round"
            />

            {/* Data point dots on coverage polygon */}
            {data.map((f, i) => {
              const { x, y } = polarToCartesian(cx, cy, maxRadius, i, count, f.coverage);
              const isHovered = hoveredIndex === i;
              return (
                <circle
                  key={`dot-${i}`}
                  cx={x}
                  cy={y}
                  r={isHovered ? 5 : 3.5}
                  fill={COVERAGE_STROKE}
                  stroke="#FFFFFF"
                  strokeWidth={isHovered ? 2 : 1.5}
                  style={{
                    transition: 'r 0.15s ease, stroke-width 0.15s ease',
                    cursor: 'pointer',
                  }}
                />
              );
            })}

            {/* Framework labels at axis endpoints */}
            {data.map((f, i) => {
              const angle = indexToAngle(i, count);
              // Push label further out from the outermost ring
              const labelRadius = maxRadius + 20;
              const lx = cx + labelRadius * Math.cos(angle);
              const ly = cy + labelRadius * Math.sin(angle);

              // Determine text anchor based on position
              const isLeft = Math.cos(angle) < -0.1;
              const isRight = Math.cos(angle) > 0.1;
              const textAnchor = isLeft ? 'end' : isRight ? 'start' : 'middle';

              // Slight vertical adjustment when at top or bottom
              const isTop = Math.sin(angle) < -0.3;
              const isBottom = Math.sin(angle) > 0.3;
              const yOffset = isTop ? -4 : isBottom ? 4 : 0;

              const coveragePct = `${Math.round(f.coverage * 100)}%`;

              return (
                <g
                  key={`label-${i}`}
                  style={{ cursor: onFrameworkClick ? 'pointer' : 'default' }}
                  onClick={() => handleLabelClick(f.name)}
                  onMouseEnter={(e) => handleMouseEnter(i, e)}
                  role="button"
                  tabIndex={0}
                  aria-label={`${f.name}: ${coveragePct} coverage`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLabelClick(f.name);
                    }
                  }}
                >
                  {/* Framework name */}
                  <text
                    x={lx}
                    y={ly + yOffset - 2}
                    textAnchor={textAnchor}
                    fontSize={LABEL_FONT_SIZE}
                    fontWeight={600}
                    fill={hoveredIndex === i ? COVERAGE_STROKE : '#333333'}
                    style={{
                      transition: 'fill 0.15s ease',
                      textDecoration: onFrameworkClick ? 'none' : undefined,
                    }}
                  >
                    {f.name}
                  </text>

                  {/* Coverage percentage */}
                  <text
                    x={lx}
                    y={ly + yOffset + PERCENT_FONT_SIZE + 2}
                    textAnchor={textAnchor}
                    fontSize={PERCENT_FONT_SIZE}
                    fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace"
                    fontWeight={500}
                    fill={hoveredIndex === i ? COVERAGE_STROKE : '#666666'}
                    style={{ transition: 'fill 0.15s ease' }}
                  >
                    {coveragePct}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Tooltip overlay */}
          {hoveredFramework && (
            <div
              className={styles.tooltip}
              style={{
                left: tooltipPos.x + 12,
                top: tooltipPos.y - 60,
              }}
            >
              <div className={styles.tooltipName}>{hoveredFramework.name}</div>
              <div className={styles.tooltipRow}>
                <span>Coverage</span>
                <span className={styles.tooltipValue}>
                  {Math.round(hoveredFramework.coverage * 100)}%
                </span>
              </div>
              {hoveredFramework.target !== undefined && (
                <div className={styles.tooltipRow}>
                  <span>Target</span>
                  <span className={styles.tooltipValue}>
                    {Math.round(hoveredFramework.target * 100)}%
                  </span>
                </div>
              )}
              {hoveredFramework.controlsTotal !== undefined && (
                <div className={styles.tooltipRow}>
                  <span>Controls</span>
                  <span className={styles.tooltipValue}>
                    {hoveredFramework.controlsSatisfied ?? 0} / {hoveredFramework.controlsTotal}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className={styles.legendRow}>
          <div className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ backgroundColor: COVERAGE_STROKE }}
            />
            <Caption1>Current</Caption1>
          </div>
          {hasTargets && (
            <div className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{
                  backgroundColor: 'transparent',
                  border: `1.5px dashed ${TARGET_STROKE}`,
                }}
              />
              <Caption1>Target</Caption1>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
